import ReactDOM from 'react-dom';
import { useTheme } from 'styled-components';
import { Root, Toast as ToastRoot } from './toast.styles';
import { Text, TextType } from '../text';
import { useTimeout } from '../../utils/timeout';
import { isBrowser } from '../../utils/env';
const getPortalTarget = () => {
    if (!isBrowser) return null;
    return document.getElementById('root') || document.body;
};
export enum ToastType {
    INFORMATION = 'information',
    ERROR = 'error',
}
export interface Notification {
    id?: string;
    title?: string;
    message: string;
    type?: ToastType;
}
export interface NotificationState {
    notifications: Notification[];
}
// Replace Function with an explicit type for remove
export interface ToastProps {
    duration?: number;
    toast: Notification;
    remove: (toast: Notification) => void;
}
export function Toast({ duration, remove, toast }: ToastProps) {
    const theme = useTheme();
    const { title, message, type = ToastType.INFORMATION } = toast;
    // Different durations based on type
    const defaultDuration = type === ToastType.ERROR ? 8000 : 5000;
    const toastDuration = duration ?? defaultDuration;
    // Automatically remove the toast after a timeout
    useTimeout(() => remove(toast), toastDuration);
    return (
        <ToastRoot
            $type={type}
            onClick={() => remove(toast)}
            // An error interrupts what a screen reader is saying; anything
            // else waits its turn.
            role={type === ToastType.ERROR ? 'alert' : 'status'}
        >
            {title && (
                <Text
                    type={TextType.descriptionBold}
                    color={type === ToastType.ERROR ? theme.textColor : theme.toastTitleColor}
                >
                    {title}
                </Text>
            )}
            <Text
                type={TextType.descriptionSmall}
                color={type === ToastType.ERROR ? theme.textColor : theme.toastColor}
            >
                {message}
            </Text>
        </ToastRoot>
    );
}
export function Toasts({
    notifications,
    remove,
}: {
    notifications: Notification[];
    remove: (toast: Notification) => void;
}) {
    const target = getPortalTarget();
    if (!target) return null;
    // The stack is a live region, and it is there before any toast is: screen
    // readers only reliably announce content added to a region that already
    // exists, not one that arrives with its content.
    return ReactDOM.createPortal(
        <Root aria-live="polite" aria-relevant="additions text">
            {notifications.map((t) => (
                <Toast key={`toast-${t.id}`} remove={remove} toast={t} />
            ))}
        </Root>,
        target
    );
}
